import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/useToast'
import { FileDown, FileUp, RefreshCw } from 'lucide-react'

type SelectionDraft = {
  applicable: boolean
  rationale: string
}

export function StigsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<Record<string, SelectionDraft>>({})
  const [scanTargetPath, setScanTargetPath] = useState('')

  const { data: missions = [] } = useQuery({
    queryKey: ['missions'],
    queryFn: () => api.platform.missions.list(),
  })

  const selectedMissionId = searchParams.get('mission') || missions[0]?.mission_id || ''

  const { data: catalog = [], isLoading: catalogLoading } = useQuery({
    queryKey: ['stigs', 'catalog'],
    queryFn: () => api.softwareiq.stigs.listCatalog(),
  })

  const { data: syncStatus } = useQuery({
    queryKey: ['stigs', 'sync-status'],
    queryFn: () => api.softwareiq.stigs.getSyncStatus(),
  })

  const { data: selections = [] } = useQuery({
    queryKey: ['stigs', 'selections', selectedMissionId],
    queryFn: async () => {
      if (!selectedMissionId) return []
      const response = await api.softwareiq.stigs.listMissionSelections(selectedMissionId)
      return response.selections || []
    },
    enabled: Boolean(selectedMissionId),
  })

  const selectionMap = useMemo(() => {
    const map = new Map<string, typeof selections[number]>()
    selections.forEach((selection) => {
      map.set(selection.catalog_id, selection)
    })
    return map
  }, [selections])

  const syncMutation = useMutation({
    mutationFn: () => api.softwareiq.stigs.syncCatalog(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['stigs', 'catalog'] })
      queryClient.invalidateQueries({ queryKey: ['stigs', 'sync-status'] })
      toast({
        title: 'STIG Catalog Synced',
        description: `${result.synced} entries refreshed from ${result.source}.`,
      })
    },
    onError: (error) => {
      toast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Unable to sync STIG catalog.',
        variant: 'destructive',
      })
    },
  })

  const selectionMutation = useMutation({
    mutationFn: ({ catalogId, payload }: { catalogId: string; payload: SelectionDraft }) =>
      api.softwareiq.stigs.upsertSelection(selectedMissionId, catalogId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stigs', 'selections', selectedMissionId] })
      toast({ title: 'Selection Saved', description: 'STIG applicability updated.' })
    },
    onError: (error) => {
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Unable to update selection.',
        variant: 'destructive',
      })
    },
  })

  const scanMutation = useMutation({
    mutationFn: () =>
      api.softwareiq.stigs.runScans(selectedMissionId, {
        target: {
          target_type: 'source',
          path: scanTargetPath || undefined,
        },
      }),
    onSuccess: (result) => {
      toast({
        title: 'STIG Scans Submitted',
        description: `${result.job_ids.length} scan jobs queued for mission.`,
      })
    },
    onError: (error) => {
      toast({
        title: 'Scan Submission Failed',
        description: error instanceof Error ? error.message : 'Unable to submit STIG scans.',
        variant: 'destructive',
      })
    },
  })

  const handleDownload = async (catalogId: string) => {
    if (!selectedMissionId) return
    try {
      const blob = await api.softwareiq.stigs.exportCkl(selectedMissionId, catalogId)
      api.softwareiq.reports.downloadBlob(blob, `stig-${catalogId}.ckl`)
    } catch (error) {
      toast({
        title: 'CKL Export Failed',
        description: error instanceof Error ? error.message : 'Unable to export CKL.',
        variant: 'destructive',
      })
    }
  }

  const handleUpload = async (catalogId: string, file: File | null) => {
    if (!selectedMissionId || !file) return
    try {
      await api.softwareiq.stigs.importCkl(selectedMissionId, catalogId, file)
      queryClient.invalidateQueries({ queryKey: ['stigs', 'selections', selectedMissionId] })
      toast({ title: 'CKL Imported', description: 'Checklist uploaded successfully.' })
    } catch (error) {
      toast({
        title: 'CKL Import Failed',
        description: error instanceof Error ? error.message : 'Unable to import CKL.',
        variant: 'destructive',
      })
    }
  }

  const handleMissionChange = (missionId: string) => {
    setSearchParams((params) => {
      params.set('mission', missionId)
      return params
    })
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">STIG Applicability</h2>
          <p className="text-muted-foreground">
            Sync DISA STIG catalog and capture mission applicability with rationale.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          Sync Catalog
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Mission Context</CardTitle>
          <Badge variant="outline">
            {syncStatus?.last_status ? `Sync: ${syncStatus.last_status}` : 'Sync: unknown'}
          </Badge>
        </CardHeader>
        <CardContent>
          <Select value={selectedMissionId} onValueChange={handleMissionChange}>
            <SelectTrigger className="max-w-md">
              <SelectValue placeholder="Select mission..." />
            </SelectTrigger>
            <SelectContent>
              {missions.map((mission) => (
                <SelectItem key={mission.mission_id} value={mission.mission_id}>
                  {mission.mission_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {syncStatus?.last_sync_at && (
            <p className="text-xs text-muted-foreground mt-2">
              Last sync: {new Date(syncStatus.last_sync_at).toLocaleString()}
            </p>
          )}
          <div className="mt-4 space-y-2 max-w-md">
            <label className="text-xs font-medium text-muted-foreground">STIG Scan Target Path</label>
            <Input
              placeholder="e.g., /workspace/source"
              value={scanTargetPath}
              onChange={(event) => setScanTargetPath(event.target.value)}
            />
            <Button
              onClick={() => scanMutation.mutate()}
              disabled={!selectedMissionId || scanMutation.isPending || !scanTargetPath.trim()}
            >
              Submit STIG Scans
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">STIG Catalog</CardTitle>
        </CardHeader>
        <CardContent>
          {catalogLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading catalog...</div>
          ) : catalog.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No STIG entries available. Run a catalog sync to seed the list.
            </div>
          ) : (
            <div className="space-y-4">
              {catalog.map((entry) => {
                const selection = selectionMap.get(entry.catalog_id)
                const draft = drafts[entry.catalog_id] || {
                  applicable: selection?.applicable ?? true,
                  rationale: selection?.rationale || '',
                }
                return (
                  <div key={entry.catalog_id} className="rounded-lg border border-border p-4 space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-semibold">{entry.title}</h3>
                        <p className="text-xs text-muted-foreground">
                          {entry.stig_id} · {entry.version || 'version n/a'} · {entry.source.toUpperCase()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleDownload(entry.catalog_id)}>
                          <FileDown className="h-4 w-4 mr-2" />
                          Export CKL
                        </Button>
                        <label className="inline-flex items-center">
                          <Input
                            type="file"
                            accept=".ckl"
                            className="hidden"
                            onChange={(event) => handleUpload(entry.catalog_id, event.target.files?.[0] || null)}
                          />
                          <Button variant="outline" size="sm" asChild>
                            <span>
                              <FileUp className="h-4 w-4 mr-2" />
                              Import CKL
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">Applicable</label>
                        <Select
                          value={draft.applicable ? 'yes' : 'no'}
                          onValueChange={(value) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [entry.catalog_id]: {
                                ...draft,
                                applicable: value === 'yes',
                              },
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">Rationale</label>
                        <Textarea
                          value={draft.rationale}
                          onChange={(event) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [entry.catalog_id]: {
                                ...draft,
                                rationale: event.target.value,
                              },
                            }))
                          }
                          rows={2}
                          placeholder="Document applicability rationale..."
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => selectionMutation.mutate({ catalogId: entry.catalog_id, payload: draft })}
                        disabled={!selectedMissionId || selectionMutation.isPending}
                      >
                        Save Selection
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
